import { Badge, Box, Button, Card, Group, Modal, Stack, Text, Textarea, Title } from "@mantine/core"
import { CalendarIcon } from "@phosphor-icons/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import useSWR from "swr"
import { DeliveryDateChangeApi } from "../../apis"
import type { IDeliveryDateChangeRequest } from "../../apis/delivery-date-change"
import { usePermission } from "../permission"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"

const STATUS_COLOR: Record<string, string> = {
  PENDING : "yellow",
  APPROVED: "green",
  REJECTED: "red",
}

const asDate = (v?: string | null) => (v ? v.slice(0, 10) : "—")

export function DeliveryDateChangePanel({ orderId, reload }: { orderId: string; reload?: () => void }) {
  const { t } = useTranslation(["supplierSales", "common"])
  const canDecide = usePermission("ORD_DN_ASSIGN")
  const [busy, setBusy] = useState(false)
  const [rejecting, setRejecting] = useState<IDeliveryDateChangeRequest | null>(null)
  const [note, setNote] = useState("")

  const { data, mutate } = useSWR(
    `delivery-date-change.${orderId}`,
    () => DeliveryDateChangeApi.list(orderId),
    { revalidateOnFocus: false },
  )

  const requests = data ?? []
  // Nothing has ever been asked for this order - no need for an empty card.
  if (requests.length === 0) return null

  const afterDecision = () => {
    mutate()
    // The order's own delivery date moves on approval, so the parent has to
    // refetch or it keeps showing the old one.
    reload?.()
  }

  const onApprove = async (request: IDeliveryDateChangeRequest) => {
    try {
      setBusy(true)
      await DeliveryDateChangeApi.approve(request.id)
      message.success(t("dateChange.approveSuccess"))
      afterDecision()
    } catch (err) {
      message.error((err as HttpHandler)?.message || t("common:messages.error"))
    } finally {
      setBusy(false)
    }
  }

  const onReject = async () => {
    if (!rejecting || !note.trim()) return
    try {
      setBusy(true)
      await DeliveryDateChangeApi.reject(rejecting.id, { decisionNote: note.trim() })
      message.success(t("dateChange.rejectSuccess"))
      setRejecting(null)
      setNote("")
      afterDecision()
    } catch (err) {
      message.error((err as HttpHandler)?.message || t("common:messages.error"))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card withBorder shadow="sm" radius="md" mb="md">
      <Group gap="xs" mb="sm">
        <CalendarIcon size={18} />
        <Title order={5}>{t("dateChange.title")}</Title>
      </Group>

      <Stack gap="sm">
        {requests.map((request) => {
          const statusColor = STATUS_COLOR[request.status] ?? "gray"
          const statusLabel = t(`dateChange.statusLabel.${request.status}`, { defaultValue: request.status })
          return (
            <Box key={request.id} p="sm" style={{ borderTop: "1px solid var(--mantine-color-gray-3)" }}>
              <Group justify="space-between" align="flex-start">
                <Box>
                  <Group gap="xs">
                    <Text size="sm" fw={600}>
                      {asDate(request.currentDeliveryDate)} → {asDate(request.requestedDeliveryDate)}
                    </Text>
                    <Badge size="sm" color={statusColor} variant="light">{statusLabel}</Badge>
                  </Group>
                  <Text size="sm" c="dimmed">
                    {t(`dateChange.side.${request.requestedBySide}`, { defaultValue: request.requestedBySide })} · {asDate(request.createdAt)}
                  </Text>
                  {request.reason && <Text size="sm" mt={4}>{request.reason}</Text>}
                  {request.decisionNote && (
                    <Text size="sm" mt={4} c="dimmed">{t("dateChange.decision")}: {request.decisionNote}</Text>
                  )}
                </Box>

                {request.status === "PENDING" && canDecide && (
                  <Group gap="xs">
                    <Button size="sm" color="green" loading={busy} onClick={() => onApprove(request)}>
                      {t("common:actions.approve")}
                    </Button>
                    <Button
                      size="sm"
                      color="red"
                      variant="light"
                      disabled={busy}
                      onClick={() => { setRejecting(request); setNote("") }}
                    >
                      {t("common:actions.reject")}
                    </Button>
                  </Group>
                )}
              </Group>
            </Box>
          )
        })}
      </Stack>

      <Modal opened={!!rejecting} onClose={() => setRejecting(null)} title={t("dateChange.rejectModalTitle")}>
        <Stack gap="sm">
          {/* The endpoint requires a note; the form must not let an empty one
              be sent, or the buyer gets a refusal with no reason. */}
          <Textarea
            label={t("dateChange.reasonLabel")}
            placeholder={t("dateChange.reasonPlaceholder")}
            minRows={3}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRejecting(null)}>{t("common:actions.dismiss")}</Button>
            <Button color="red" loading={busy} disabled={!note.trim()} onClick={onReject}>
              {t("common:actions.reject")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  )
}
